/**
 * Admin & Settings screen (M8) — Users & Access + tenant configuration.
 *
 * Two panels, layered top-down:
 *   1. Users & Access — the authoritative team/user management experience,
 *      rendered by {@link UsersAccessScreen}. It owns Users (with a Property
 *      access column, Add member → property access All/Selected + checklist →
 *      one-time Temporary Password card, Manage drawer, Generate temporary
 *      password, Deactivate/Reactivate), Roles & Permissions, Invitations, and
 *      Sessions & Security, all talking to the enforced /api/access/* backend.
 *   2. Settings panel (M8) — a form listing every known setting key with its
 *      current value, each editable and saved via setSetting. A VALIDATION
 *      reason surfaces through the shared {@link UserMessage}.
 *
 * All backend access for the settings panel goes through the fetch-based
 * `../services.js` layer (web boundary: no `@avos/core-api` import). API error
 * envelopes surface through the shared accessible {@link UserMessage} component.
 *
 * SSR-safe: the initial render is a pure loading state; every network call runs
 * inside an effect or an event handler, so the server render matches hydration.
 */
import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../../components/UserMessage.js";
import {
  apiGetSettings,
  apiSetSetting,
  asError,
  type TenantSetting,
} from "../services.js";
import UsersAccessScreen from "./UsersAccessScreen.js";

/** The known tenant setting keys, with human labels + input hints. */
const SETTING_FIELDS: readonly {
  readonly key: string;
  readonly label: string;
  readonly placeholder: string;
}[] = [
  { key: "display_currency", label: "Display currency", placeholder: "INR" },
  { key: "timezone", label: "Timezone", placeholder: "Asia/Kolkata" },
  { key: "check_in_time", label: "Check-in time", placeholder: "14:00" },
  { key: "check_out_time", label: "Check-out time", placeholder: "11:00" },
  {
    key: "booking_lead_days",
    label: "Booking lead days",
    placeholder: "0",
  },
  {
    key: "cancellation_policy",
    label: "Cancellation policy",
    placeholder: "Full refund up to 7 days before check-in",
  },
];

// ===========================================================================
// Settings panel (M8)
// ===========================================================================

function SettingField({
  fieldKey,
  label,
  placeholder,
  value,
  onSave,
  busy,
}: {
  readonly fieldKey: string;
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  readonly onSave: (key: string, value: string) => void;
  readonly busy: boolean;
}): ReactElement {
  const [draft, setDraft] = useState(value);

  // Keep the draft in sync when the loaded value changes (e.g. after a refresh).
  useEffect(() => {
    setDraft(value);
  }, [value]);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    onSave(fieldKey, draft);
  }

  return (
    <form className="console-inline-form" onSubmit={handleSubmit} noValidate>
      <div className="console-field">
        <label htmlFor={`admin-setting-${fieldKey}`}>{label}</label>
        <input
          id={`admin-setting-${fieldKey}`}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
        />
      </div>
      <button
        type="submit"
        className="console-btn console-btn--ghost"
        disabled={busy}
      >
        {busy ? "Saving…" : "Save"}
      </button>
    </form>
  );
}

function SettingsPanel({
  settings,
  onSave,
  savingKey,
}: {
  readonly settings: readonly TenantSetting[];
  readonly onSave: (key: string, value: string) => void;
  readonly savingKey: string | null;
}): ReactElement {
  const byKey: Record<string, string> = {};
  for (const s of settings) {
    byKey[s.key] = s.value;
  }

  return (
    <section className="console-panel" aria-label="Tenant settings">
      <h2 className="console-panel__title">Settings</h2>
      <p className="recon-caption">
        Tenant-level configuration. Each value is validated when you save it —
        an invalid value is explained and nothing is changed.
      </p>

      {SETTING_FIELDS.map((field) => (
        <SettingField
          key={field.key}
          fieldKey={field.key}
          label={field.label}
          placeholder={field.placeholder}
          value={byKey[field.key] ?? ""}
          onSave={onSave}
          busy={savingKey === field.key}
        />
      ))}
    </section>
  );
}

// ===========================================================================
// Top-level screen
// ===========================================================================

type LoadState =
  | { readonly kind: "loading" }
  | {
      readonly kind: "ready";
      readonly settings: readonly TenantSetting[];
    }
  | { readonly kind: "error" };

export default function AdminScreen(): ReactElement {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const [status, setStatus] = useState<UserMessageModel | null>(null);

  const [savingKey, setSavingKey] = useState<string | null>(null);

  const load = useCallback(async (): Promise<{
    settings: readonly TenantSetting[];
  }> => {
    const settings = await apiGetSettings();
    return { settings };
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const { settings } = await load();
      setState({ kind: "ready", settings });
    } catch {
      setState({ kind: "error" });
    }
  }, [load]);

  useEffect(() => {
    let active = true;
    load()
      .then(({ settings }) => {
        if (active) {
          setState({ kind: "ready", settings });
        }
      })
      .catch(() => {
        if (active) {
          setState({ kind: "error" });
        }
      });
    return () => {
      active = false;
    };
  }, [load]);

  const handleSaveSetting = useCallback(
    async (key: string, value: string): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setSavingKey(key);
      try {
        const result = await apiSetSetting({ key, value });
        const e = asError(result);
        if (e !== null) {
          setApiError(toUserMessage(e));
          return;
        }
        setStatus({
          what: "Setting saved.",
          why: `"${key}" has been updated.`,
          next: "No action required.",
        });
        await refresh();
      } catch {
        setApiError(networkError("save the setting"));
      } finally {
        setSavingKey(null);
      }
    },
    [refresh],
  );

  return (
    <section aria-labelledby="admin-heading" data-console-screen="admin">
      <p className="console-page-eyebrow">Account · Settings</p>
      <h1 id="admin-heading">Admin &amp; Settings</h1>
      <p className="console-lede">
        Manage your team and their roles, configure tenant-level settings, and
        review what each role can do.
      </p>

      <UsersAccessScreen />

      {state.kind === "loading" ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading admin data…
        </p>
      ) : null}

      {state.kind === "error" ? (
        <p className="console-empty" role="status">
          Admin data is unavailable right now. Try refreshing in a moment.
        </p>
      ) : null}

      {state.kind === "ready" ? (
        <>
          {apiError !== null ? (
            <UserMessage message={apiError} severity="error" />
          ) : null}
          {status !== null ? (
            <UserMessage message={status} severity="status" />
          ) : null}

          <SettingsPanel
            settings={state.settings}
            onSave={(key, value) => void handleSaveSetting(key, value)}
            savingKey={savingKey}
          />
        </>
      ) : null}
    </section>
  );
}

/** A consistent network-failure UserMessage for a failed action. */
function networkError(action: string): UserMessageModel {
  return {
    what: `Could not ${action}.`,
    why: "The request to the admin service failed.",
    next: "Check the API server is running and try again.",
  };
}
